import {
  useGoogleAnalyticsSetup,
  usePendoSetup,
  useZendeskSetup,
} from "@certn/utils";
import * as Sentry from "@sentry/browser";
import { useLDClient, withLDProvider } from "launchdarkly-react-client-sdk";
import React, { useEffect, useMemo } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Route, Routes } from "react-router-dom";

import "./yup.config";
import { useUser } from "./hooks/api/useUser";
import { useLanguage } from "./hooks/useLanguage";
import "./i18n";
import "./index.css";
import { DeclineRequest } from "./pages/DeclineRequest/DeclineRequest";
import { RequestDeclined } from "./pages/DeclineRequest/RequestDeclined";
import { Providers } from "./providers";
import { AuthOutlet } from "./routes/AuthOutlet";
import { AuthRoutes } from "./routes/AuthRoutes";
import { CheckFlowRoutes } from "./routes/CheckFlowRoutes";
import { AppRoutes } from "./routes/Routes";
import { legacyPortalUserToVisitor } from "./utils";
import constants from "../constants";

Sentry.init({
  dsn: `https://4d5d5a3a21ab4f6bb54c227459e2c96f@o132497.ingest.sentry.io/4504799240585216`,
  environment: constants.VITE_SENTRY_ENVIRONMENT,
  release: constants.VITE_SENTRY_RELEASE,
  enabled: !!constants.VITE_SENTRY_ENVIRONMENT,
  ignoreErrors: ["ResizeObserver loop"],
});

const App = () => {
  const { user } = useUser();
  const ldClient = useLDClient();

  useGoogleAnalyticsSetup({
    userId: user?.id,
    measurementIds: constants.VITE_GA_MEASUREMENT_IDS?.split(" ") || [],
  });
  const pendoVisitor = useMemo(() => legacyPortalUserToVisitor(user), [user]);
  usePendoSetup({ visitor: pendoVisitor });
  useZendeskSetup();
  useLanguage();

  useEffect(() => {
    if (user?.id) {
      ldClient?.identify({
        kind: "user",
        key: user.id,
        name: user?.email || "",
        is_consumer: true,
      });
    }
  }, [user]);

  return (
    <Routes>
      <Route path="/auth/*" element={<AuthRoutes />} />
      <Route element={<AuthOutlet />}>
        <Route
          path="/apply/:applicantId/:sectionName"
          element={<CheckFlowRoutes />}
        />
        <Route path="/apply/:applicantId/*" element={<CheckFlowRoutes />} />
      </Route>
      <Route element={<AuthOutlet />}>
        <Route path="*" element={<AppRoutes />} />
      </Route>
      <Route
        path="/:applicantId/decline-request"
        element={<DeclineRequest />}
      />
      <Route path="/request-declined" element={<RequestDeclined />} />
    </Routes>
  );
};

const AppWithLDProvider = withLDProvider({
  clientSideID: constants.VITE_LAUNCH_DARKLY_KEY,
  context: {
    kind: "user",
    key: "anonymous-user-id",
    anonymous: true,
    is_consumer: true,
  },
})(App);

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  <React.StrictMode>
    <BrowserRouter>
      <Providers>
        <AppWithLDProvider />
      </Providers>
    </BrowserRouter>
  </React.StrictMode>
);
